import { Injectable, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { Confirmation } from '../../components/confirmation/confirmation';

type ErrorDialogKind = 'api-error' | 'circuit-breaker' | 'session-expired';

@Injectable({
  providedIn: 'root'
})
export class ErrorDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isDialogOpen = signal(false);
  private readonly activeDialogKind = signal<ErrorDialogKind | null>(null);
  private activeDialogRef: MatDialogRef<Confirmation> | null = null;
  private dialogSequence = 0;

  openApiError(message?: string, supportCode?: string): void {
    this.openDialog(
      'We could not complete that request',
      this.withSupportCode(message ?? 'The action you tried has failed. Please try again.', supportCode),
      'error',
      'Got it',
      'api-error'
    );
  }

  /** Show the "Session expired" dialog.
   *  @param onSignIn called when the user clicks "Sign in again". */
  openSessionExpired(onSignIn: () => void): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // If a session-expired dialog is already open, do nothing (guard)
    if (this.isDialogOpen() && this.activeDialogKind() === 'session-expired') return;

    // Close any other open dialog and replace with the session-expired one
    this.activeDialogRef?.close();

    const sequence = ++this.dialogSequence;
    this.isDialogOpen.set(true);
    this.activeDialogKind.set('session-expired');

    this.activeDialogRef = this.dialog.open(Confirmation, {
      disableClose: true,
      data: {
        header: 'Session expired',
        type: 'warning',
        content: 'Your sign-in has timed out. Sign in again to continue.',
        showProceed: true,
        proceedLabel: 'Sign in again',
      }
    });

    this.activeDialogRef.afterClosed().subscribe((result: string) => {
      if (sequence !== this.dialogSequence) return;
      this.isDialogOpen.set(false);
      this.activeDialogKind.set(null);
      this.activeDialogRef = null;
      if (result === 'proceed') onSignIn();
    });
  }

  openCircuitBreakerOpen(remainingSeconds: number): void {
    const seconds = remainingSeconds > 0 ? remainingSeconds : 60;
    this.openDialog(
      'Please wait before trying again',
      `Repeated API errors triggered a short pause. Please wait about ${seconds} seconds, then try the action again.`,
      'warning',
      'OK',
      'circuit-breaker',
      true
    );
  }

  private openDialog(
    header: string,
    content: string,
    type: 'error' | 'warning',
    confirm: string,
    kind: ErrorDialogKind,
    replaceExisting = false
  ): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.isDialogOpen()) {
      if (!replaceExisting || this.activeDialogKind() === kind) {
        return;
      }

      this.activeDialogRef?.close();
    }

    const sequence = ++this.dialogSequence;
    this.isDialogOpen.set(true);
    this.activeDialogKind.set(kind);

    this.activeDialogRef = this.dialog.open(Confirmation, {
      data: {
        header,
        type,
        content,
        confirm
      }
    });

    this.activeDialogRef
      .afterClosed()
      .subscribe(() => {
        if (sequence !== this.dialogSequence) {
          return;
        }

        this.isDialogOpen.set(false);
        this.activeDialogKind.set(null);
        this.activeDialogRef = null;
      });
  }

  private withSupportCode(message: string, supportCode?: string): string {
    if (!supportCode) {
      return message;
    }

    return `${message}\n\nSupport code: ${supportCode}`;
  }
}
